import { isNullOrUndef, isNullOrUndefOrEmpty } from "@civicscience/chops";

import { NilOr } from "types";

type GetWeightingSchemeOrDefaultArgs = {
  wsName: NilOr<string>;
  fallback: string | null;
};

/**
 * The value used to represent the "Account Default" in select searches.
 * This is ONLY valid when the list of weighting schemes is configured to include the account default.
 *
 * See `useAllWeightingSchemes` query.
 */
const ACCOUNT_DEFAULT_VAL = "";

/**
 * The value used to represent "Unweighted" in select searches.
 * This is ONLY valid when the list of weighting schemes is configured to include the account default.
 *
 * See `useAllWeightingSchemes` query.
 */
const UNWEIGHTED_WITH_DEFAULT_VAL = "-";

/**
 * Helper to create the selected value for a select search control.
 */
const getWeightingSchemeSelection = (name: NilOr<string>, fallback: NilOr<string>) => {
  return !isNullOrUndef(name) ? [name] : isNullOrUndef(fallback) ? undefined : [fallback];
};

/**
 * Resolves the provided weighting scheme name factoring in the provided default/fallback.
 *
 * Typically, this is used when your list of weighting schemes includes the account default.
 * In that case, options for both "Account Default" and "Unweighted" are included in the list.
 * You can provide the account default as the fallback option.
 * In this scenario, only the special `UNWEIGHTED_WITH_DEFAULT_VAL` value will result in an empty/unweighted return.
 */
const getWeightingSchemeOrDefault = ({ wsName = null, fallback }: GetWeightingSchemeOrDefaultArgs): string | null => {
  if (wsName === UNWEIGHTED_WITH_DEFAULT_VAL) {
    return null;
  }

  if (isNullOrUndefOrEmpty(wsName)) {
    return fallback;
  }

  return wsName;
};

/**
 * Returns `true` if the provided `wsName` represents the "Unweighted" value.
 */
const isUnweighted = (wsName: NilOr<string> = null): boolean => wsName === UNWEIGHTED_WITH_DEFAULT_VAL;

export default {
  ACCOUNT_DEFAULT_VAL,
  getWeightingSchemeSelection,
  getWeightingSchemeOrDefault,
  isUnweighted,
};
